/* eslint-disable react/jsx-props-no-spreading, react-hooks/exhaustive-deps, radix, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import {
  Table as ShellTable,
  TBody,
  THead,
  TD as Td,
  TH as Th,
  TR as Tr,
} from '@sede-enterprise/shell-ds-react-framework';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { v4 as uuidv4 } from 'uuid';

import { PAGINATION_OPTIONS } from '../../../../constants/Common';
import Loader from '../../../../UI/Loader';
import ColumnTitle from './ColumnTitle';
import PaginationSection from './PaginationSection';
import RenderFilters from './RenderFilters';
import { IColumn, ISorting, ITableFilter, ITableProps, ITableRef, IValue } from './Table.type';
import { TableLoaderContainer, TableWrapper } from './style';

const MAX_HEIGHT = 350;
const RECORD_PER_PAGE = 10;

const Table = forwardRef<ITableRef, React.PropsWithChildren<ITableProps<any>>>((props, ref) => {
  const {
    className = '',
    loading = false,
    data = [],
    columns = [],
    totalRecords,
    defaultFilters,
    paginationOptions,
    recordPerPage = RECORD_PER_PAGE,
    maxHeight = MAX_HEIGHT,
    fixedHeight = false,
    pagination = true,
    isServerSide = false,
    onChangeSortingPaginationFilter = () => {},
    onTableStateChange,
    getFilterPopupContainer,
  } = props;

  const isFirstServerSideEffectRef = React.useRef(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(recordPerPage);
  const [tableData, setTableData] = useState<any[]>(data);
  const [sortedColumn, setSortedColumn] = useState<ISorting>({
    orderBy: '',
    sort: 'asc',
  });

  const paginationOption: number[] = paginationOptions || PAGINATION_OPTIONS.FIVE_TO_TWO_FIFTY;

  const [tableFilters, setTableFilters] = useState<ITableFilter>({
    pageNumber: 1,
    pageSize: recordPerPage,
    orderBy: null,
    sort: null,
    filters: defaultFilters || [],
  });

  const { pageNumber, pageSize, orderBy, sort, filters } = tableFilters;

  useEffect(() => {
    if (!isServerSide) {
      onTableStateChange?.({
        pageNumber: currentPage,
        pageSize: currentPageSize,
        orderBy: sortedColumn.orderBy || null,
        sort: sortedColumn.sort || null,
        filters: tableFilters.filters ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isServerSide,
    currentPage,
    currentPageSize,
    sortedColumn.orderBy,
    sortedColumn.sort,
    tableFilters.filters,
  ]);

  // Helper function to check if a value is "empty" (null, undefined, empty string, or whitespace)
  const isEmpty = (value: unknown): boolean => {
    return value == null || value === '' || (typeof value === 'string' && value.trim() === '');
  };

  const compareEmptyValues = (aIsEmpty: boolean, bIsEmpty: boolean): number | null => {
    if (aIsEmpty && bIsEmpty) {
      return 0;
    }
    if (aIsEmpty) {
      return 1;
    }
    if (bIsEmpty) {
      return -1;
    }
    return null;
  };

  const compareValues = (aVal: unknown, bVal: unknown): number => {
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.toLowerCase().localeCompare(bVal.toLowerCase());
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return aVal - bVal;
    }
    if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
      return aVal !== bVal && aVal ? 1 : -1;
    }
    if (aVal instanceof Date && bVal instanceof Date) {
      return aVal.getTime() - bVal.getTime();
    }
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    return aStr.localeCompare(bStr);
  };

  const applySortToData = <T,>(dataToSort: T[], key: keyof T, order: string): T[] => {
    if (!order || !key) {
      return dataToSort;
    }

    return [...dataToSort].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      const aIsEmpty = isEmpty(aVal);
      const bIsEmpty = isEmpty(bVal);

      const emptyComparison = compareEmptyValues(aIsEmpty, bIsEmpty);
      if (emptyComparison !== null) {
        return emptyComparison;
      }

      const comparison = compareValues(aVal, bVal);
      return order === 'asc' ? comparison : -comparison;
    });
  };

  useEffect(() => {
    if (isServerSide) {
      setTableData(data);
    } else {
      const currentSortKey = sortedColumn.orderBy;
      const currentSortOrder = sortedColumn.sort;

      if (currentSortKey && currentSortOrder && currentSortKey !== '') {
        const sortedData = applySortToData(data, currentSortKey, currentSortOrder);
        setTableData(sortedData);
      } else {
        setTableData(data);
      }
    }
  }, [data, isServerSide, sortedColumn.orderBy, sortedColumn.sort]);

  // keep pagination within bounds when the (filtered) data set shrinks/grows
  useEffect(() => {
    if (isServerSide) {
      return;
    }
    const maxPage = Math.max(1, Math.ceil(data.length / currentPageSize));
    setCurrentPage((prevPage) => (prevPage > maxPage ? 1 : prevPage));
  }, [data, isServerSide, currentPageSize]);

  const handlePaginationChange = (page: number) => {
    if (isServerSide) {
      setTableFilters((prevState) => ({
        ...prevState,
        pageNumber: page,
      }));
    } else {
      setCurrentPage(page);
    }
  };

  const applySort = (key: string, order: string) => {
    if (isServerSide) {
      setTableFilters((prevState) => ({
        ...prevState,
        orderBy: order ? key : null,
        sort: order,
      }));
      return;
    }

    setSortedColumn({ orderBy: key, sort: order });

    if (order) {
      const sortedData = applySortToData(data, key, order);
      setTableData(sortedData);
    } else {
      setTableData([...data]);
    }
  };

  const handlePageSizeChange = (value: number) => {
    if (isServerSide) {
      setTableFilters((prevState) => ({
        ...prevState,
        pageNumber: 1,
        pageSize: value,
      }));
    } else {
      setCurrentPage(1);
      setCurrentPageSize(value);
    }
  };

  const applyFilter = (
    key: string,
    fieldName: string,
    searchValue: IValue | undefined | IValue[],
    displayLabel?: string,
    displayValue?: string | string[],
  ) => {
    let appliedFilters = [...filters];

    if (typeof searchValue === 'object' && searchValue.length === 0) {
      appliedFilters = appliedFilters.filter((item) => item.key !== key);
    } else {
      const result = appliedFilters.find((_) => _.key === key);
      if (result) {
        appliedFilters = appliedFilters.map((_) => {
          if (_.key === key) {
            return {
              key,
              fieldName,
              value: searchValue,
              displayLabel,
              displayValue,
            };
          }
          return _;
        });
      } else {
        appliedFilters.push({
          key,
          fieldName,
          value: searchValue,
          displayLabel,
          displayValue,
        });
      }
    }

    if (!isServerSide) {
      setCurrentPage(1);
    }

    setTableFilters((prevState) => ({
      ...prevState,
      filters: appliedFilters,
      pageNumber: 1,
    }));
  };

  const columnHelper = createColumnHelper<any>();

  const renderColumnTitle = (column: IColumn) => (
    <ColumnTitle
      column={column}
      sortedColumn={
        isServerSide
          ? {
              orderBy,
              sort,
            }
          : sortedColumn
      }
      applySort={applySort}
      applyFilter={applyFilter}
      filteredColumn={filters.find((item) => item.key === column.accessorKey)}
      getPopupContainer={getFilterPopupContainer}
    />
  );

  const shellColumns = columns
    .filter((column) => !!column)
    .map((column, index) => {
      return columnHelper.accessor(column.accessorKey, {
        ...column,
        // A stable id (not time-based) keeps react-table from treating columns as brand new on
        // every render, which was unmounting/remounting header filter popups and losing focus.
        // Falls back to the column index when accessorKey is empty (e.g. action/button columns),
        // since tanstack requires a non-empty id when the header is a function, not a string.
        id: column.accessorKey || `column-${index}`,
        footer: () => flexRender(column.footer, {}),
        header: () => renderColumnTitle(column),
      });
    });

  useEffect(() => {
    if (!isServerSide) {
      return;
    }

    if (isFirstServerSideEffectRef.current) {
      isFirstServerSideEffectRef.current = false;
      return;
    }

    onChangeSortingPaginationFilter({
      pageNumber,
      pageSize,
      orderBy,
      sort,
      filters,
    });
  }, [isServerSide, pageNumber, pageSize, orderBy, sort, filters]);

  const getTableFilters = () => {
    return {
      pageNumber,
      pageSize,
      orderBy,
      sort,
      filters,
    };
  };

  const resetPagination = () => {
    if (isServerSide) {
      setTableFilters((prevState) => ({
        ...prevState,
        pageNumber: 1,
      }));
    } else {
      setCurrentPage(1);
    }
  };

  const refreshData = () => {
    if (pageNumber === 1) {
      onChangeSortingPaginationFilter({
        pageNumber,
        pageSize,
        orderBy,
        sort,
        filters,
      });
    } else {
      setTableFilters((prevState) => ({
        ...prevState,
        pageNumber: 1,
      }));
    }
  };

  const handleClearFiltersOnClick = () => {
    setTableFilters((prevState) => ({
      ...prevState,
      pageNumber: 1,
      filters: [],
    }));
  };

  const handleClearFilter = (key: string) => {
    setTableFilters((prevState) => ({
      ...prevState,
      filters: prevState.filters.filter((_) => _.key !== key),
    }));
  };

  const handleClearArrayFilter = (key: string, valueIndex: number) => {
    const result = filters.find((filter) => filter.key === key);

    if (result && Array.isArray(result.value) && result.value.length === 1) {
      handleClearFilter(key);
      return;
    }

    const updatedFilters = filters.map((filter) => {
      if (filter.key !== key) {
        return filter;
      }

      const newValue = Array.isArray(filter.value)
        ? filter.value.filter((_, index) => index !== valueIndex)
        : [];

      const newDisplayValue = Array.isArray(filter.displayValue)
        ? filter.displayValue.filter((_, index) => index !== valueIndex)
        : [];

      return {
        ...filter,
        value: newValue,
        displayValue: newDisplayValue,
      };
    });

    setTableFilters((prevState) => ({
      ...prevState,
      filters: updatedFilters,
    }));
  };

  // ✅ compute paginated data here so ref methods can return it
  const paginatedData = React.useMemo(
    () =>
      isServerSide || !pagination
        ? tableData
        : tableData.slice((currentPage - 1) * currentPageSize, currentPage * currentPageSize),
    [isServerSide, pagination, currentPage, currentPageSize, tableData],
  );

  useImperativeHandle(ref, () => ({
    getTableFilters,
    refreshData,
    handleClearArrayFilter,
    resetPagination,
    getVisibleRows: () => paginatedData,
  }));

  const table = useReactTable({
    data: paginatedData,
    columns: shellColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableWrapper maxHeight={maxHeight} fixedHeight={fixedHeight} className={className}>
      {loading && (
        <TableLoaderContainer>
          <div data-testid='loader' className='loader-wrapper'>
            <Loader />
          </div>
        </TableLoaderContainer>
      )}
      <div>
        {filters.length > 0 && (
          <RenderFilters
            handleClearFiltersOnClick={handleClearFiltersOnClick}
            handleClearArrayFilter={handleClearArrayFilter}
            handleClearFilter={handleClearFilter}
            filters={filters}
          />
        )}
        <div className='table-container'>
          <ShellTable
            className={`shell-table ${className}`}
            data-testid='table'
            size='small'
            alignment='center'
          >
            <THead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={uuidv4()}>
                  {headerGroup.headers.map((header) => (
                    <Th key={`table-head-${uuidv4()}`} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </THead>
            <TBody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={`table-row-${uuidv4()}`}
                  style={row.original.style}
                  className={row.original.className}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={uuidv4()}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </TBody>
            {!table.getRowModel().rows.length && (
              <TBody>
                <Tr>
                  <Td colSpan={shellColumns.length} className='no-data-cell'>
                    <div className='title_wrapper'>
                      {totalRecords === 0 && !loading ? (
                        <div>
                          <img width={80} src='/Lighthouse.svg' alt='lighthouse' />
                          <div>No data found!</div>
                        </div>
                      ) : null}
                    </div>
                  </Td>
                </Tr>
              </TBody>
            )}
          </ShellTable>
        </div>
        {pagination && data.length > 0 && (
          <PaginationSection
            currentPage={isServerSide ? (pageNumber ?? 1) : currentPage}
            pageSize={isServerSide ? (pageSize ?? 1) : currentPageSize}
            totalRecords={totalRecords ?? data.length}
            onPageChange={handlePaginationChange}
            onPageSizeChange={handlePageSizeChange}
            paginationOptions={paginationOption}
          />
        )}
      </div>
    </TableWrapper>
  );
});

export default Table;
